import React, { useState, useEffect, useRef } from "react";
import { Sun, Moon, Menu, X, Globe } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useLang, useT } from "../context/LanguageContext";
import Logo from "./Logo";

const Navbar = ({ onTalkAgent }) => {
  const { theme, toggleTheme } = useTheme();
  const { lang, toggleLang } = useLang();
  const t = useT();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [navHidden, setNavHidden] = useState(false);
  const hideTimer = useRef(null);
  const loginTarget = (process.env.REACT_APP_LOGIN_URL || "https://app.verasist.ai").trim();

  const handleLoginClick = () => {
    const target = loginTarget || "https://app.verasist.ai";
    const normalizedTarget =
      target.startsWith("http://") || target.startsWith("https://")
        ? target
        : `https://${target}`;

    window.location.assign(normalizedTarget);
  };

  // Show nav on activity, hide after inactivity
  useEffect(() => {
    const showNav = () => {
      setNavHidden(false);
      clearTimeout(hideTimer.current);
      hideTimer.current = setTimeout(() => {
        if (window.scrollY > 200) {
          setNavHidden(true);
        }
      }, 3000);
    };

    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      showNav();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("mousemove", showNav, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("mousemove", showNav);
      clearTimeout(hideTimer.current);
    };
  }, []);

  const navLinks = [
    { label: t("nav.product"), href: "/#product" },
    { label: t("nav.stack"), href: "/#stack" },
    { label: t("nav.integrations"), href: "/#integrations" },
    { label: t("nav.industries"), href: "/#industries" },
    { label: t("references.title"), href: "/references", highlight: true },
    { label: t("nav.pricing"), href: "/pricing" },
    { label: t("nav.faq"), href: "/#faq" },
    { label: t("nav.docs"), href: "https://docs.verasist.ai", external: true },
  ];

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 px-2 sm:px-4 pt-2 sm:pt-4 transition-transform duration-400 ease-in-out"
      style={{ transform: navHidden ? "translateY(-120%)" : "translateY(0)" }}
    >
      <div
        className={`max-w-6xl mx-auto flex items-center justify-between glass-nav rounded-full px-3 sm:px-6 py-2 sm:py-2.5 transition-all duration-300 ${
          scrolled ? "shadow-[0_8px_30px_rgba(30,42,82,0.10)]" : "shadow-none"
        }`}
      >
        <a href="/" className="flex shrink-0 items-center pl-1">
          <Logo size={30} />
        </a>

        <nav className="hidden xl:flex items-center gap-3">
          {navLinks.map((l) => (
            <a key={l.label} href={l.href} className={`${l.highlight ? "neon-btn rounded-full px-3 py-1.5" : "nav-link"} whitespace-nowrap text-[13px] font-medium`} style={{ color: "var(--fg)" }}
              {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <button
            onClick={handleLoginClick}
            className="neon-btn hidden sm:inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-semibold"
          >
            {t("nav.login")}
          </button>

          <button
            onClick={toggleLang}
            aria-label="Toggle language"
            className="neon-btn inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-wider"
          >
            <Globe size={12} /> {lang === "tr" ? "TR" : "EN"}
          </button>

          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="neon-btn relative w-12 h-7 rounded-full flex items-center px-1"
          >
            <span
              className="w-5 h-5 rounded-full flex items-center justify-center transition-transform duration-300"
              style={{
                background: "var(--neon)",
                transform: theme === "dark" ? "translateX(20px)" : "translateX(0)",
                boxShadow: "0 0 10px var(--neon)",
              }}
            >
              {theme === "dark" ? <Moon size={12} color="#060918" /> : <Sun size={12} color="#0B112C" />}
            </span>
          </button>

          <button className="xl:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Menu" aria-expanded={mobileOpen} style={{ color: "var(--fg)" }}>
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="xl:hidden max-w-6xl mx-auto mt-2 glass-nav rounded-2xl px-4 py-4 flex flex-col gap-3">
          {navLinks.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className={`${l.highlight ? "neon-btn self-start rounded-full px-4 py-2" : "py-1"} text-[14px] font-medium`}
              style={{ color: "var(--fg)" }}
              onClick={() => setMobileOpen(false)}
              {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            >
              {l.label}
            </a>
          ))}
          <button
            onClick={() => {
              setMobileOpen(false);
              handleLoginClick();
            }}
            className="neon-btn mt-1 rounded-full px-4 py-2 text-[13px] font-semibold"
          >
            {t("nav.login")}
          </button>
        </div>
      )}
    </header>
  );
};

export default Navbar;
